import React, { Fragment, useEffect, useRef, useState } from "react";
import { CardBody, Col, Row, Table } from "reactstrap";
import { Link } from "react-router-dom";

import {
  ColumnFiltersState,
  FilterFn,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  PaginationState
} from '@tanstack/react-table';

import { rankItem } from '@tanstack/match-sorter-utils';

// Global Filter (DebouncedInput - sin cambios, pero lo modificaremos para búsqueda en servidor)
const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) => {
  const [value, setValue] = useState(initialValue);
  const onChangeRef = useRef(onChange);
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  // Mantener una referencia estable al último onChange
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      // Usar la referencia estable evita re-disparos por nueva identidad de onChange
      onChangeRef.current(value);
    }, debounce);

    return () => clearTimeout(timeoutRef.current);
  }, [debounce, value]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      onChangeRef.current(value);
    }
    if (props.onKeyDown) {
      props.onKeyDown(e);
    }
  };

  return (
    <input {...props} value={value} id="search-bar-0" className="form-control search" onChange={e => setValue(e.target.value)} onKeyDown={handleKeyDown} />
  );
};

interface TableContainerProps {
  columns?: any;
  data?: any;
  isGlobalFilter?: boolean;
  customPageSize?: number;
  tableClass?: string;
  theadClass?: string;
  divClass?: string;
  SearchPlaceholder?: string;
  isCustomPagination?: boolean;
  pageCount?: number;
  totalCount?: number;
  onPageChange?: (page: number) => void;
  onSearch?: (searchText: string) => void;
  loading?: boolean;
  pageIndex?: number;
  additionalFilters?: React.ReactNode;
}

const TableContainer = ({
  columns,
  data,
  isGlobalFilter,
  customPageSize,
  tableClass,
  theadClass,
  divClass,
  SearchPlaceholder,
  isCustomPagination = false,
  pageCount: controlledPageCount,
  totalCount,
  onPageChange,
  onSearch,
  loading,
  pageIndex: customPageIndex,
  additionalFilters,
}: TableContainerProps) => {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState('');

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: customPageIndex ?? 0,
    pageSize: Number(customPageSize) || 10,
  });

  // Sincronizar el estado de paginación cuando cambia customPageIndex
  useEffect(() => {
    if (isCustomPagination && customPageIndex !== undefined) {
      setPagination(prev => ({ ...prev, pageIndex: customPageIndex }));
    }
  }, [customPageIndex, isCustomPagination]);

  const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({ itemRank });
    return itemRank.passed;
  };

  const table = useReactTable({
    columns,
    data,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      columnFilters,
      globalFilter: isCustomPagination ? undefined : globalFilter,
      pagination: {
        pageIndex: isCustomPagination ? (customPageIndex ?? 0) : pagination.pageIndex,
        pageSize: Number(customPageSize) || 10
      }
    },
    autoResetPageIndex: false, // Important for server-side pagination
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: isCustomPagination ? undefined : setPagination, // Only set local pagination if not custom
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: isCustomPagination,
    pageCount: isCustomPagination ? controlledPageCount : undefined,
  });

  const {
    getHeaderGroups,
    getRowModel,
    getCanPreviousPage,
    getCanNextPage,
    getState,
    getPageCount
  } = table;

  const { pageIndex } = getState().pagination;
  const [jumpPage, setJumpPage] = useState<string | number>(pageIndex + 1);

  useEffect(() => {
    setJumpPage(pageIndex + 1);
  }, [pageIndex]);

  const handlePageChange = (newPage: number) => {
    if (onPageChange) {
      onPageChange(newPage);
    }
  };

  const handleJumpPage = () => {
    const page = Number(jumpPage) - 1;
    const totalPages = getPageCount();
    if (!isNaN(page) && page >= 0 && page < totalPages) {
      handlePageChange(page);
    } else {
      setJumpPage(pageIndex + 1);
    }
  };

  return (
    <Fragment>
      {isGlobalFilter && <Row className="mb-3">
        <CardBody className="border border-dashed border-end-0 border-start-0">
          <form onSubmit={(e) => e.preventDefault()}>
            <Row>
              <Col sm={5}>
                <div className="search-box me-2 mb-2 d-inline-block col-12">
                  <DebouncedInput
                    value={globalFilter ?? ''}
                    onChange={value => {
                      if (isCustomPagination && onSearch) {
                        onSearch(String(value));
                      } else {
                        setGlobalFilter(String(value));
                      }
                    }}
                    placeholder={SearchPlaceholder}
                  />
                  <i className="bx bx-search-alt search-icon"></i>
                </div>
              </Col>
              {additionalFilters && (
                <Col sm={7}>
                  {additionalFilters}
                </Col>
              )}
            </Row>
          </form>
        </CardBody>
      </Row>}

      <div className={divClass}>
        <Table hover className={tableClass}>
          <thead className={theadClass}>
            {getHeaderGroups().map((headerGroup: any) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header: any) => (
                  <th key={header.id} {...{
                    onClick: header.column.getToggleSortingHandler(),
                  }}>
                    {header.isPlaceholder ? null : (
                      <React.Fragment>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: ' ',
                          desc: ' ',
                        }
                        [header.column.getIsSorted() as string] ?? null}
                      </React.Fragment>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </td>
              </tr>
            ) : getRowModel().rows.map((row: any) => {
              return (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell: any) => {
                    return (
                      <td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>

      <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-3">

        <div className="text-muted mb-2 mb-sm-0">
          Mostrando
          <span className="fw-semibold mx-1">
            {getRowModel().rows.length}
          </span>
          de
          <span className="fw-semibold mx-1">
            {isCustomPagination ? totalCount : data.length}
          </span>
          Resultados
        </div>

        <div className="d-flex align-items-center gap-3 flex-wrap">
          <div className="d-flex align-items-center">
            <span className="me-2 text-muted" style={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>Ir a:</span>
            <input
              type="number"
              className="form-control form-control-sm"
              style={{ width: "60px", padding: '0.2rem 0.5rem' }}
              value={jumpPage}
              onChange={(e) => setJumpPage(e.target.value)}
              onBlur={handleJumpPage}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleJumpPage();
                }
              }}
            />
          </div>

          <ul className="pagination pagination-separated pagination-md mb-0">

            <li className={!getCanPreviousPage() || loading ? "page-item disabled" : "page-item"}>
              <Link
                to="#"
                className="page-link"
                onClick={(e) => {
                  e.preventDefault();
                  if (getCanPreviousPage()) {
                    handlePageChange(pageIndex - 1);
                  }
                }}
              >
                Anterior
              </Link>
            </li>

            {(() => {
              const totalPages = getPageCount() || 0;
              const visiblePages = 3;

              let start = Math.max(0, pageIndex - 1);
              let end = start + visiblePages;

              if (end > totalPages) {
                end = totalPages;
                start = Math.max(0, end - visiblePages);
              }

              const pages = [];
              for (let i = start; i < end; i++) {
                pages.push(i);
              }

              return pages.map((item) => (
                <li className="page-item" key={item}>
                  <Link
                    to="#"
                    className={pageIndex === item ? "page-link active" : "page-link"}
                    onClick={(e) => {
                      e.preventDefault();
                      handlePageChange(item);
                    }}
                  >
                    {item + 1}
                  </Link>
                </li>
              ));
            })()}

            <li className={!getCanNextPage() || loading ? "page-item disabled" : "page-item"}>
              <Link
                to="#"
                className="page-link"
                onClick={(e) => {
                  e.preventDefault();
                  if (getCanNextPage()) {
                    handlePageChange(pageIndex + 1);
                  }
                }}
              >
                Siguiente
              </Link>
            </li>

          </ul>
        </div>

      </div>
    </Fragment>
  );
};

export default TableContainer;
